/**
 * TemplatePicker — "From library" dialog: pick the Discovr default or a brand
 * template to duplicate into this campaign. Archived rows are hidden — they're
 * gone from every picker, not just the campaign's own library sections. The
 * global library is not listed; only the one default the backend falls back to.
 */
import type { OutreachTemplate, OutreachTemplateGroups } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { KIND_LABELS } from './TemplateCard';
import { DEFAULT_TEMPLATE_LABEL, defaultSequenceTemplate } from './defaultSequenceTemplate';

export interface TemplatePickerProps {
  open: boolean;
  groups: OutreachTemplateGroups;
  onPick: (t: OutreachTemplate) => void;
  onClose: () => void;
}

export function TemplatePicker({ open, groups, onPick, onClose }: TemplatePickerProps) {
  const fallback = defaultSequenceTemplate(groups);
  const rows = [...(fallback ? [fallback] : []), ...groups.brand.filter((t) => !t.archivedAt)];
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Start from the library</DialogTitle>
          <DialogDescription>Pick the Discovr default or a brand template to copy into this campaign.</DialogDescription>
        </DialogHeader>
        {rows.length === 0 ? (
          <p className="text-body-sm-regular text-muted-foreground">No templates to copy yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {rows.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-3 py-2.5">
                <span className="min-w-0">
                  <span className="block truncate text-body-sm-medium text-foreground">{t.scope === 'global' ? DEFAULT_TEMPLATE_LABEL : t.name}</span>
                  <span className="text-[12px] text-muted-foreground">
                    {t.scope === 'global' ? 'Default' : 'Brand'} · {KIND_LABELS[t.kind]} · {t.steps.length} steps
                  </span>
                </span>
                <Button size="sm" variant="outline" aria-label={`Use ${t.scope === 'global' ? DEFAULT_TEMPLATE_LABEL : t.name}`} onClick={() => onPick(t)}>
                  Use
                </Button>
              </li>
            ))}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}
